import { Box } from '@mui/material';
import { type JSX } from 'react';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { DocumentApi } from 'Api/Document/DocumentApi';
import { Button } from 'Components/Buttons/Buttons';
import { CircleIndicatorControlEffectiveness } from 'Components/CircleIndicators/CircleIndicatorControlEffectiveness';
import { CircleIndicatorRiskRating } from 'Components/CircleIndicators/CircleIndicatorRiskRating';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { FileManagementArea, FileManagementAreaHookValues } from 'Components/Files/FileManagementArea';
import { ChangeEventType, FormFieldSelect } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { FormFieldTextAreaAi } from 'Components/FormField/FormFieldTextAreaAi/FormFieldTextAreaAi';
import { Text } from 'Components/Text/Text';
import { GENERIC_ERROR_MESSAGE } from 'Config/Errors';
import { iso8601ToUsTimestampLong } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { UserNameFormat, getUserNameFromSubject } from 'Helpers/UserUtils';
import { useAiContentAdvisor } from 'Hooks/AiContentAdvisor';
import { AiContentAdvisorFeature, AiDueDiligenceAnalysisStatus, AiDueDiligenceAssessmentAnalysisMetadata, AiDueDiligenceFinalReviewAnalysisMetadata, DueDiligenceReviewAi } from 'Models/ArtificialIntelligence';
import { EffectivenessSelectOptions } from 'Models/OperationalControls';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { RiskRating, RiskRatingSelectOptions } from 'Models/RiskRating';

import styles from './ReviewForm.module.css';
import { SummitGuideOverwriteNotice } from '../SummitGuideOverwriteNotice/SummitGuideOverwriteNotice';

interface ReviewFormPropsBase {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    documentApi: DocumentApi;
    fileManagementHookValues: FileManagementAreaHookValues;
    inherentRisk: RiskRating;
    module: DueDiligenceModule;
    onAdditionalInformationChange: (value: string) => void;
    onAiError: (message: string) => void;
    onOverallEffectivenessChange: (value: number | undefined) => void;
    onResidualRiskChange: (value: number | undefined) => void;

    onClickSummitGuideButton: () => void;

    additionalInformation?: string;
    disabled?: boolean;
    overallEffectiveness?: number;
    residualRisk?: number;
    savedOverallEffectiveness?: number;
    savedResidualRisk?: number;
}

interface AssessmentReviewFormProps extends ReviewFormPropsBase {
    _type: 'Assessment';
    aiReview?: DueDiligenceReviewAi;
    aiReviewMetadata?: AiDueDiligenceAssessmentAnalysisMetadata;
}

interface FinalReviewReviewFormProps extends ReviewFormPropsBase {
    _type: 'FinalReview';
    aiReview?: DueDiligenceReviewAi;
    aiReviewMetadata?: AiDueDiligenceFinalReviewAnalysisMetadata;
}

export type ReviewFormProps = AssessmentReviewFormProps | FinalReviewReviewFormProps;

/** Renders a consistent UI for displaying and manually facilitating SummIT Guide™ analyses. Used for both assessment Reviews and the assessment subject's Final Review. */
export const ReviewForm = (props: ReviewFormProps) => {
    const cachedData = useCachedData();

    // The Additional Information field is shared between the assessment Review and the assessment subject's Final Review, but each maps to a distinct AI Content Advisor feature.
    const additionalInformationFeature = props.module === ProductModule.UCRM ? (props._type === 'Assessment' ? AiContentAdvisorFeature.UCRM_ASSESSMENT_REVIEW_ADDITIONAL_INFORMATION : AiContentAdvisorFeature.UCRM_FINAL_REVIEW_ADDITIONAL_INFORMATION) : props._type === 'Assessment' ? AiContentAdvisorFeature.TPRM_ASSESSMENT_REVIEW_ADDITIONAL_INFORMATION : AiContentAdvisorFeature.TPRM_FINAL_REVIEW_ADDITIONAL_INFORMATION;
    const additionalInformationAi = useAiContentAdvisor(props.artificialIntelligenceApi, props.additionalInformation ?? '', additionalInformationFeature, props.onAiError);

    // If there is an existing analysis, but a new one has been requested and hasn't yet completed, then hide the existing analysis. It is confusing to see "in progress" information above a result.
    const aiReviewIsDisplayed = props.aiReview !== undefined && !(props.aiReviewMetadata && props.aiReviewMetadata.status !== AiDueDiligenceAnalysisStatus.SUCCEEDED);

    return (
        <>
            <Box component="form">
                <div className={styles.container}>
                    <div className={styles.selectionRowAndNotice}>
                        <div className={styles.selectionRow}>
                            <div className={styles.selection}>
                                {/* The assessment subject's inherent risk is displayed in a drop-down for visual consistency, but it can't be edited. */}
                                <FormFieldSelect disabled options={RiskRatingSelectOptions} handleChange={() => void 0} formFieldId="inherentRisk" selectedOption={props.inherentRisk} formFieldLabel="Inherent Risk" />
                            </div>
                            <div className={styles.selection}>
                                <FormFieldSelect
                                    disabled={props.disabled}
                                    options={EffectivenessSelectOptions}
                                    handleChange={(value: ChangeEventType): void => {
                                        props.onOverallEffectivenessChange(value as number);
                                    }}
                                    formFieldId="overallEffectiveness"
                                    selectedOption={props.overallEffectiveness}
                                    formFieldLabel="Overall Control Effectiveness"
                                />
                            </div>
                            <div className={styles.selection}>
                                <FormFieldSelect
                                    disabled={props.disabled}
                                    required
                                    options={RiskRatingSelectOptions}
                                    handleChange={(value: ChangeEventType): void => {
                                        props.onResidualRiskChange(value as number);
                                    }}
                                    formFieldId="residualRisk"
                                    selectedOption={props.residualRisk}
                                    formFieldLabel="Residual Risk"
                                />
                            </div>
                        </div>
                        {aiReviewIsDisplayed && (
                            <SummitGuideOverwriteNotice
                                comparisons={[
                                    { recommended: props.aiReview?.recommended_effectiveness, saved: props.savedOverallEffectiveness },
                                    { recommended: props.aiReview?.recommended_residual_risk, saved: props.savedResidualRisk },
                                ]}
                            />
                        )}
                    </div>
                    <div>
                        <Text variant="Header4">SummIT Guide™ Analysis</Text>
                        <div className={styles.summitGuideGenerationStatus}>
                            {props.aiReviewMetadata && (
                                <div className={styles.aiReviewMetadata}>
                                    <Text noStyles>
                                        Analysis initiated by {props.aiReviewMetadata.created_by ? getUserNameFromSubject(props.aiReviewMetadata.created_by, cachedData.users, UserNameFormat.FIRST_SPACE_LAST) : 'Auto-Advance'} at {iso8601ToUsTimestampLong(props.aiReviewMetadata.created_timestamp)}.
                                    </Text>
                                    {((): JSX.Element => {
                                        switch (props.aiReviewMetadata.status) {
                                            case AiDueDiligenceAnalysisStatus.IN_PROGRESS:
                                                return <Text noStyles>Please refresh the webpage in a few minutes to view the resulting analysis.</Text>;
                                            case AiDueDiligenceAnalysisStatus.FAILED:
                                                return (
                                                    <Text noStyles color="red">
                                                        Analysis did not complete successfully. {GENERIC_ERROR_MESSAGE}
                                                    </Text>
                                                );
                                            case AiDueDiligenceAnalysisStatus.SUCCEEDED:
                                                return <></>;
                                        }
                                    })()}
                                    {props._type === 'Assessment' && props.aiReview && props.aiReviewMetadata.status === AiDueDiligenceAnalysisStatus.SUCCEEDED && (
                                        <>
                                            <Text noStyles>{`${props.module === ProductModule.UCRM ? 'Respondent' : 'Third-party'} responses were ${props.aiReviewMetadata.include_third_party_responses ? 'included' : 'not included'}.`}</Text>
                                            <Text noStyles>Internal responses were {props.aiReviewMetadata.include_client_responses ? 'included' : 'not included'}.</Text>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                        {aiReviewIsDisplayed && props.aiReview && (
                            <div>
                                <div className={styles.circleRow}>
                                    <Text noStyles>Recommended overall control effectiveness:</Text>
                                    <CircleIndicatorControlEffectiveness effectiveness={props.aiReview.recommended_effectiveness} />
                                </div>
                                {props.aiReview.recommended_residual_risk !== undefined && (
                                    <div className={styles.circleRow}>
                                        <Text noStyles>Recommended residual risk:</Text>
                                        <CircleIndicatorRiskRating riskRating={props.aiReview.recommended_residual_risk} />
                                    </div>
                                )}
                                <div className={styles.aiReviewComments}>
                                    <Text preserveLineBreaks noStyles>
                                        {props.aiReview.comments}
                                    </Text>
                                </div>
                            </div>
                        )}
                        {!props.aiReview && !props.aiReviewMetadata && <Text noStyles>No analysis has been performed.</Text>}
                        {props.disabled !== true && props.aiReviewMetadata?.status !== AiDueDiligenceAnalysisStatus.IN_PROGRESS && (
                            <Button variant="linkText" size="sm" onClick={props.onClickSummitGuideButton}>
                                Perform new analysis
                            </Button>
                        )}
                    </div>
                    <FormFieldTextAreaAi
                        disabled={props.disabled}
                        formFieldId="additionalInformation"
                        handleChange={(event: React.FormEvent<HTMLInputElement>): void => {
                            props.onAdditionalInformationChange(event.currentTarget.value);
                        }}
                        formFieldLabel="Additional Information"
                        value={props.additionalInformation}
                        aiContentAdvisorValues={additionalInformationAi}
                    />
                    <div>
                        <FileManagementArea fileDragAndDropFormFieldLabel="Supporting Documentation" disabled={props.disabled ?? false} documentApi={props.documentApi} fileManagementHookValues={props.fileManagementHookValues} />
                    </div>
                </div>
            </Box>
        </>
    );
};
